import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrowOutlined';
import StopIcon from '@mui/icons-material/StopOutlined';
import TranslateIcon from '@mui/icons-material/TranslateOutlined';
import { translate, type TranslateField, type TranslateModel, type TranslateHealth, type TranslatePreview } from '../api/client';
import { fmtInt } from '../lib/format';

type Props = {
  open: boolean;
  onClose: () => void;
  team: string | null;   // aktueller globaler Team-Filter
};

type Phase = 'setup' | 'running' | 'done';

export function TranslateDialog({ open, onClose, team }: Props) {
  const [fields, setFields] = useState<Set<TranslateField>>(new Set(['expectedBehaviour']));
  const [model, setModel]   = useState<TranslateModel>('qwen2.5:7b');
  const [health, setHealth] = useState<TranslateHealth | null>(null);
  const [preview, setPreview] = useState<TranslatePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('setup');
  const [jobId, setJobId] = useState<string | null>(null);
  const [processed, setProcessed] = useState(0);
  const [total, setTotal] = useState(0);
  const [runError, setRunError] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number>(0);
  const evtRef = useRef<EventSource | null>(null);

  // Health einmalig beim Öffnen
  useEffect(() => {
    if (!open) return;
    void translate.health().then(setHealth).catch(() => setHealth({ ok: false, models: [], error: 'offline' }));
  }, [open]);

  // Preview neu wenn Auswahl / Team ändert
  const fieldsArr = useMemo(() => Array.from(fields), [fields]);
  const loadPreview = useCallback(async () => {
    if (!fields.size) { setPreview({ count: 0, chars: 0, perField: [] }); return; }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const p = await translate.preview(fieldsArr, team);
      setPreview(p);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewLoading(false);
    }
  }, [fields, fieldsArr, team]);

  useEffect(() => {
    if (!open || phase !== 'setup') return;
    void loadPreview();
  }, [open, phase, loadPreview]);

  // SSE-Stream einrichten wenn jobId gesetzt
  useEffect(() => {
    if (!jobId) return;
    const es = new EventSource(translate.streamUrl(jobId), { withCredentials: true });
    evtRef.current = es;
    es.addEventListener('state', (e) => {
      const d = JSON.parse((e as MessageEvent).data);
      setTotal(d.total ?? 0);
      setProcessed(d.processed ?? 0);
      if (d.error) setRunError(d.error);
      if (d.status === 'done' || d.status === 'cancelled' || d.status === 'error') {
        setPhase('done');
      }
    });
    es.addEventListener('progress', (e) => {
      const d = JSON.parse((e as MessageEvent).data);
      setProcessed(d.processed ?? 0);
      setTotal(d.total ?? 0);
    });
    es.addEventListener('done', () => {
      es.close();
      setPhase('done');
    });
    es.onerror = () => {
      // SSE-Fehler ignorieren, `done` schließt sowieso
    };
    return () => { es.close(); };
  }, [jobId]);

  function toggleField(f: TranslateField) {
    const next = new Set(fields);
    if (next.has(f)) next.delete(f); else next.add(f);
    setFields(next);
  }

  async function start() {
    if (!fields.size) return;
    setRunError(null);
    setProcessed(0);
    try {
      const r = await translate.start(fieldsArr, team, model);
      setJobId(r.jobId);
      setTotal(r.total);
      setStartedAt(Date.now());
      setPhase('running');
    } catch (e) {
      setRunError(e instanceof Error ? e.message : String(e));
    }
  }

  async function cancel() {
    if (!jobId) return;
    try { await translate.cancel(jobId); } catch { /* ignore */ }
  }

  function resetAndClose() {
    if (evtRef.current) { evtRef.current.close(); evtRef.current = null; }
    setPhase('setup');
    setJobId(null);
    setProcessed(0);
    setTotal(0);
    setRunError(null);
    onClose();
  }

  // ETA berechnen
  const eta = useMemo(() => {
    if (phase !== 'running' || processed === 0 || total === 0) return null;
    const elapsedMs = Date.now() - startedAt;
    const perItem = elapsedMs / processed;
    const remainingMs = perItem * (total - processed);
    return formatDuration(remainingMs);
  }, [phase, processed, total, startedAt]);

  const busy = phase === 'running';
  const modelsAvailable = health?.models ?? [];
  const has7b  = modelsAvailable.some(m => m.startsWith('qwen2.5:7b'));
  const has14b = modelsAvailable.some(m => m.startsWith('qwen2.5:14b'));

  return (
    <Dialog open={open} onClose={busy ? undefined : resetAndClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 4 } } }}>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TranslateIcon color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">In-App-Übersetzung DE → EN</Typography>
            <Typography variant="caption" color="text.secondary">
              Lokal via Ollama · deutscher Text bleibt erhalten (append)
              {team && <> · Team: <b>{team}</b></>}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {/* Health-Status */}
        {health && !health.ok && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Ollama nicht erreichbar: {health.error ?? 'unbekannter Fehler'}. Prüfe ob Ollama läuft
            (<code>ollama serve</code>) und Modelle installiert sind.
          </Alert>
        )}
        {health && health.ok && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Ollama läuft · verfügbare Modelle: {health.models.slice(0, 5).join(', ')}
          </Alert>
        )}

        {phase === 'setup' && (
          <Stack spacing={2}>
            {/* Feld-Auswahl */}
            <FormControl>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Welche Felder übersetzen?</Typography>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={fields.has('expectedBehaviour')}
                    onChange={() => toggleField('expectedBehaviour')} />}
                  label="Expected Behaviour"
                />
                <FormControlLabel
                  control={<Checkbox checked={fields.has('summary')}
                    onChange={() => toggleField('summary')} />}
                  label="Summary (Titel)"
                />
                <FormControlLabel
                  control={<Checkbox disabled checked={false} />}
                  label={<span style={{ opacity: 0.5 }}>Description — nicht im Snapshot importiert</span>}
                />
              </FormGroup>
            </FormControl>

            {/* Modell-Auswahl */}
            <FormControl>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Modell</Typography>
              <RadioGroup value={model} onChange={(e) => setModel(e.target.value as TranslateModel)}>
                <FormControlLabel
                  value="qwen2.5:7b"
                  control={<Radio />}
                  disabled={!has7b}
                  label={<>Qwen 2.5 7B <Typography component="span" variant="caption" color="text.secondary">— schneller, gute Qualität</Typography></>}
                />
                <FormControlLabel
                  value="qwen2.5:14b"
                  control={<Radio />}
                  disabled={!has14b}
                  label={<>Qwen 2.5 14B <Typography component="span" variant="caption" color="text.secondary">— langsamer, bessere Qualität</Typography></>}
                />
              </RadioGroup>
            </FormControl>

            {/* Preview */}
            {previewLoading && <LinearProgress />}
            {previewError && <Alert severity="error">{previewError}</Alert>}
            {preview && !previewLoading && (
              <Alert severity={preview.count === 0 ? 'info' : 'success'} icon={false}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Wird übersetzt:</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" color="primary" label={`${fmtInt(preview.count)} Feld-Einträge`} />
                  <Chip size="small" variant="outlined" label={`${fmtInt(preview.chars)} Zeichen`} />
                  {preview.perField.map(pf => (
                    <Chip
                      key={pf.field}
                      size="small"
                      variant="outlined"
                      label={`${pf.field === 'summary' ? 'Summary' : 'EB'}: ${fmtInt(pf.count)}`}
                    />
                  ))}
                </Stack>
                {preview.count > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Nur Tickets mit deutschem Text · Team-Filter oben wirkt · Grobe Zeit-Schätzung:{' '}
                    <b>{formatDuration(preview.count * (model === 'qwen2.5:14b' ? 3000 : 1500))}</b>
                  </Typography>
                )}
              </Alert>
            )}
          </Stack>
        )}

        {(phase === 'running' || phase === 'done') && (
          <Stack spacing={2}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {phase === 'running' && `Übersetze… ${fmtInt(processed)} / ${fmtInt(total)}`}
                  {phase === 'done' && `Fertig · ${fmtInt(processed)} von ${fmtInt(total)} übersetzt`}
                </Typography>
                {eta && phase === 'running' && (
                  <Typography variant="caption" color="text.secondary">ETA: {eta}</Typography>
                )}
              </Stack>
              <LinearProgress
                variant="determinate"
                value={total > 0 ? (processed / total) * 100 : 0}
                sx={{ height: 10, borderRadius: 1 }}
              />
            </Box>
            {runError && <Alert severity="error">{runError}</Alert>}
            {phase === 'done' && !runError && (
              <Alert severity="success">
                Übersetzung abgeschlossen. Klick auf „CSV herunterladen" — der deutsche Text
                bleibt im Feld, die englische Übersetzung wird darunter angehängt (Trenner
                <code> ---\n**EN:**\n </code>). Label <code>translated-en</code> wird gesetzt.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {phase === 'setup' && (
          <>
            <Button onClick={resetAndClose}>Abbrechen</Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              disabled={!preview || preview.count === 0 || !fields.size || !health?.ok}
              onClick={start}
            >
              Übersetzung starten
            </Button>
          </>
        )}
        {phase === 'running' && (
          <>
            <Button startIcon={<StopIcon />} color="error" onClick={cancel}>Abbrechen</Button>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Fenster kann offen bleiben — Job läuft im Backend weiter
            </Typography>
          </>
        )}
        {phase === 'done' && (
          <>
            <Button onClick={resetAndClose}>Schließen</Button>
            <Box sx={{ flex: 1 }} />
            {jobId && processed > 0 && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                component="a"
                href={translate.downloadUrl(jobId)}
              >
                CSV herunterladen
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

function formatDuration(ms: number): string {
  if (!isFinite(ms) || ms < 0) return '–';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return `${m} min ${rs.toString().padStart(2, '0')} s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h} h ${rm.toString().padStart(2, '0')} min`;
}
